import React, { useState, useEffect } from 'react';
import { Percent, Smartphone, Copy, CheckCircle, Clock, Flame } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLOR_THEMES = {
  gold:   { bg: 'rgba(212,175,55,0.12)', border: '#D4AF37', text: '#D4AF37', glow: 'rgba(212,175,55,0.3)', iconBg: '#D4AF37' },
  rose:   { bg: 'rgba(255,107,157,0.12)', border: '#FF6B9D', text: '#FF6B9D', glow: 'rgba(255,107,157,0.3)', iconBg: '#FF6B9D' },
  green:  { bg: 'rgba(76,175,80,0.12)',   border: '#4CAF50', text: '#4CAF50', glow: 'rgba(76,175,80,0.3)',   iconBg: '#4CAF50' },
  blue:   { bg: 'rgba(33,150,243,0.12)',  border: '#2196F3', text: '#2196F3', glow: 'rgba(33,150,243,0.3)',  iconBg: '#2196F3' },
  red:    { bg: 'rgba(255,68,68,0.12)',   border: '#FF4444', text: '#FF4444', glow: 'rgba(255,68,68,0.3)',   iconBg: '#FF4444' },
  purple: { bg: 'rgba(156,39,176,0.12)',  border: '#9C27B0', text: '#9C27B0', glow: 'rgba(156,39,176,0.3)',  iconBg: '#9C27B0' },
};

const Countdown = ({ expiresAt, color }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const calc = () => {
      const diff = new Date(expiresAt) - new Date();
      if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      return {
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      };
    };
    setTimeLeft(calc());
    const interval = setInterval(() => setTimeLeft(calc()), 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  const units = [
    { val: timeLeft.days, label: 'j' },
    { val: timeLeft.hours, label: 'h' },
    { val: timeLeft.minutes, label: 'm' },
    { val: timeLeft.seconds, label: 's' },
  ];

  return (
    <div className="flex items-center gap-1.5 mt-3">
      <Clock size={14} style={{ color }} />
      <div className="flex gap-1">
        {units.map((u, i) => (
          <span key={i} className="text-xs font-mono font-bold px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.08)', color }}>
            {String(u.val).padStart(2, '0')}{u.label}
          </span>
        ))}
      </div>
    </div>
  );
};

const PromoSection = () => {
  const [promos, setPromos] = useState([]);
  const [copiedCode, setCopiedCode] = useState(null);

  useEffect(() => {
    const fetchPromos = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/promos/active`);
        setPromos(res.data.promos || []);
      } catch {
        // silent
      }
    };
    fetchPromos();
  }, []);

  const copyCode = (code) => {
    navigator.clipboard.writeText(code).catch(() => {});
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  if (promos.length === 0) return null;

  return (
    <section className="py-16 bg-gradient-to-b from-black to-zinc-900" data-testid="promo-section">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10">
          <h2
            className="text-4xl sm:text-5xl font-bold text-white mb-3"
            style={{ fontFamily: 'Crimson Text, serif' }}
          >
            Offres <span className="text-[#D4AF37]">Spéciales</span>
          </h2>
          <p className="text-base text-white/60 flex items-center justify-center gap-2">
            <Smartphone size={18} className="text-[#D4AF37]" />
            Montrez le code sur votre téléphone en magasin
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {promos.map((promo, index) => {
            const theme = COLOR_THEMES[promo.color_theme] || COLOR_THEMES.gold;
            const isLimited = promo.is_unlimited === false;
            const hasExpiry = !!promo.expires_at;

            return (
              <div
                key={promo.code}
                className={`relative rounded-2xl p-6 transition-all duration-300 group ${isLimited ? 'animate-pulse-subtle' : ''}`}
                style={{
                  background: theme.bg,
                  border: `1.5px solid ${theme.border}`,
                  boxShadow: isLimited ? `0 0 20px ${theme.glow}, 0 0 40px ${theme.glow}` : 'none',
                }}
                data-testid={`promo-card-${index}`}
              >
                {/* Badges */}
                <div className="absolute top-3 right-3 flex gap-1.5">
                  {isLimited && (
                    <span
                      className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold animate-bounce-slow"
                      style={{ background: theme.border, color: '#000' }}
                    >
                      <Flame size={10} />
                      Limité
                    </span>
                  )}
                  {!isLimited && (
                    <span
                      className="px-2 py-0.5 rounded-full text-xs font-bold border"
                      style={{ borderColor: `${theme.border}66`, color: theme.text, background: `${theme.border}15` }}
                    >
                      Illimité
                    </span>
                  )}
                </div>

                {/* Discount */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2.5 rounded-xl" style={{ background: theme.iconBg }}>
                    <Percent size={22} className="text-black" />
                  </div>
                  <div className="text-3xl font-black" style={{ color: theme.text }}>
                    -{promo.discount_percent}%
                  </div>
                </div>

                {promo.description && (
                  <p className="text-white/80 text-sm mb-3">{promo.description}</p>
                )}

                {/* Countdown for limited promos with expiry */}
                {hasExpiry && <Countdown expiresAt={promo.expires_at} color={theme.text} />}

                {/* Code button */}
                <button
                  onClick={() => copyCode(promo.code)}
                  className="w-full flex items-center justify-center gap-2 rounded-xl py-3 px-4 mt-3 transition-all duration-200"
                  style={{
                    background: `${theme.border}15`,
                    border: `2px dashed ${theme.border}80`,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = `${theme.border}25`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = `${theme.border}15`; }}
                  data-testid={`copy-code-${promo.code}`}
                >
                  {copiedCode === promo.code ? (
                    <>
                      <CheckCircle size={18} className="text-green-400" />
                      <span className="text-green-400 font-bold text-sm">Copié !</span>
                    </>
                  ) : (
                    <>
                      <span className="font-mono font-bold text-lg tracking-widest text-white">{promo.code}</span>
                      <Copy size={16} className="text-white/50" />
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes pulse-subtle {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.01); }
        }
        .animate-pulse-subtle {
          animation: pulse-subtle 2s ease-in-out infinite;
        }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-2px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 1.5s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default PromoSection;
